import { CommonModule } from '@angular/common'
import { Component, inject, OnInit } from '@angular/core'
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router'
import { ENV, LoggerService } from "@danangelus/ngx-app-core"
import { MenuItem } from 'primeng/api'
import { BreadcrumbModule } from 'primeng/breadcrumb'
import { MenubarModule } from 'primeng/menubar'
import { Ripple } from "primeng/ripple"
import { AuthenticationService } from './core/security/authentication.service'
import { SessionService } from './core/security/session.service'
import { AppManagerService } from "./core/services/app-manager.service"
import { Script } from "./models/gateway.model"
import { User } from './models/user.model'
import { filter } from 'rxjs'
import { VersionCheckService, APP_VERSION } from './core/services/version-check.service'

const log = LoggerService.get('AppComponent')

/**
 * Route application component provides base functionality and menu
*
* @author Dan Bennett
*/
@Component({
    selector: 'app-root',
    standalone: true,
    imports: [
        BreadcrumbModule,
        CommonModule,
        MenubarModule,
        RouterOutlet,
        Ripple,
        RouterLink
    ],
    templateUrl: './app.component.html',
    styleUrl: './app.component.sass'
})
export class AppComponent implements OnInit {

    // ToDo :: Make this more comprehensive
    languageCodes = new Map([
        [ "en_GB", "English (GB)" ],
        [ "fr_FR", "French"       ],
        [ "de_DE", "German"       ],
        [ "it_IT", "Italian"      ],
    ])
    router = inject(Router)
    appManagerService = inject(AppManagerService)
    sessionService = inject(SessionService)
    authenticationService = inject(AuthenticationService)
    versionCheckService = inject(VersionCheckService)

    environment = 'local'

    breadcrumbs: MenuItem[] = [
        { label: 'Gateway', routerLink: '/home' },
    ]

    menu: MenuItem[] = []

    private accountManagement: MenuItem[] = [{
        id: 'account',
        label: 'Account',
        iconClass: 'pi-chevron-down',
        items: [
            { id: 'change-password', label: 'Change Password', routerLink: '/account/change-password' },
            { id: 'manage-account', label: 'Manage Account', routerLink: '/account/manage-account' }
        ]
    }]

    private lastLinks: MenuItem[] = [
        {
            id: 'help',
            label: 'Help',
            iconClass: 'pi-chevron-down',
            items: [
                { id: 'cookie-policy', label: 'Cookie Policy', routerLink: '/policy/cookies' },
                { id: 'privacy-policy', label: 'Privacy Policy', routerLink: '/policy/privacy' },
            ]
        },
        {
            id: 'logout-sub',
            label: 'Logout',
            routerLink: '/logout'
        },
    ]

    private additionalLinks2w: MenuItem[] = [{ id: '2w-links', label: '2W Links', routerLink: '/2w-links' }]
    private additionalLinks2wLite: MenuItem[] = [{ id: '2w-lite', label: '2W Lite', routerLink: '/2w-lite' }]
    private additionalLinks4w: MenuItem[] = [{ id: '4w-links', label: '4W Links', routerLink: '/4w-links' }]
    private additionalLinksATV: MenuItem[] = [{ id: 'atv-links', label: 'ATV Links', routerLink: '/atv-links' }]
    private additionalLinksMarine: MenuItem[] = [{ id: 'marine-links', label: 'Marine Links', routerLink: '/marine-links' }]

    constructor() {
        this.router.events.pipe(
            filter((e: any) => e instanceof NavigationEnd)
        )
        .subscribe(() => this.versionCheckService.check())
    }

    get version(): string {
        return APP_VERSION || this.environment
    }

    ngOnInit(): void {
        this.environment = ENV.config.get('environment') || 'local'
        this.applyScripts()
        this.applyMenu()
        AuthenticationService.authenticated.subscribe((user: User | null) => {
            this.sessionService.user.set(user!)
            this.applyMenu()
        })

    }

    private applyScripts(): void {
        const scripts: Script[] | undefined = ENV.config.get('scripts')
        if (scripts?.length) {
            scripts.forEach((script: Script) => {
                const s: HTMLScriptElement = document.createElement('script')
                if (script.content) s.text = script.content!
                if (script.src) s.src = script.src!
                s.type = script.type!
                s.async = true

                if (script.params) {
                    Object.keys(script.params).forEach((key: string) => {
                        s.setAttribute(key, script.params[key])
                    })
                }

                document.head.appendChild(s)
            })
        }
    }

    private applyMenu() {
        this.menu = []
        const userGroups = this.sessionService.user()?.groups
        if (this.sessionService.user()?.verified && !this.sessionService.user()?.passwordExpired) {
            this.menu.push(...this.accountManagement)
            if (userGroups?.includes('DEALERADMIN')) {
                this.menu.push({
                    id: 'administration',
                    label: 'Administration',
                    link: '/administration',
                    iconClass: 'pi-chevron-down',
                    items: [
                        { label: 'Manage My Users', routerLink: '/administration/sub-user' },
                    ]
                })

            } else if (userGroups?.includes('DCSADMINISTRATOR')) {
                this.menu.push({
                    id: 'administration',
                    label: 'Administration',
                    link: '/administration',
                    iconClass: 'pi-chevron-down',
                    items: [
                        {
                            id: 'hq-functions',
                            label: 'HQ Functions',
                            iconClass: 'pi-chevron-right',
                            items: [
                                { id: 'edit-login-message', label: 'Edit Login Message', routerLink: '/administration/hq/login-message' },
                                { id: 'enable-disable-dcs', label: 'Enable/Disable DCS Login', routerLink: '/administration/hq/dcs-toggle' }
                            ]
                        },
                        { id: 'manage-applications', label: 'Manage Applications', routerLink: '/administration/application' },
                        { id: 'manage-groups', label: 'Manage Groups', routerLink: '/administration/group' },
                        { id: 'manage-users', label: 'Manage Users', routerLink: '/administration/user' },
                    ]
                })

                if (userGroups?.includes('DCS_VIEW_ACTIVEUSERS')) {
                    const adminMenu = this.menu.filter((item: MenuItem) => item.id === 'administration')
                    if (adminMenu.length > 0) {
                        adminMenu[0].items?.unshift({
                            id: 'active-users',
                            label: 'Active Users',
                            routerLink: '/administration/active-users'
                        })
                    }
                }
            }
            if (userGroups?.includes('DCSADMINISTRATOR')) {
                this.menu.push(...this.additionalLinks2w)
                this.menu.push(...this.additionalLinks2wLite)
                this.menu.push(...this.additionalLinks4w)
                this.menu.push(...this.additionalLinksATV)
                this.menu.push(...this.additionalLinksMarine)
            } else {
                if (
                    userGroups?.includes('ADDITIONAL2W') ||
                    userGroups?.includes('3_PROD_2WONROAD') ||
                    userGroups?.includes('3_PROD_2WOFFROAD')
                ) {
                    this.menu.push(...this.additionalLinks2w)
                    this.menu.push(...this.additionalLinks2wLite)
                }
                if (
                    userGroups?.includes('ADDITIONAL4W') ||
                    userGroups?.includes('3_PROD_4W')
                ) {
                    this.menu.push(...this.additionalLinks4w)
                }
                if (
                    userGroups?.includes('ADDITIONALATV') ||
                    userGroups?.includes('3_PROD_ATV')
                ) {
                    this.menu.push(...this.additionalLinksATV)
                }
                if (
                    userGroups?.includes('ADDITIONALMARINE') ||
                    userGroups?.includes('3_PROD_MARINE')
                ) {
                    this.menu.push(...this.additionalLinksMarine)
                }
            }
            this.menu.push(...this.lastLinks)
        }
    }

    convertLanguageCode() {
        const code = this.sessionService.user()?.preferredLanguage || 'en_GB'
        return this.languageCodes.get(code)
    }

    async launchApplication(event: Event) {
        const expertCode = (event.target as HTMLInputElement).value
        log.info('launchApplication', `Finding Application with expertCode "${expertCode}"`)
        const match = await this.appManagerService.getApplicationByExpertCode(expertCode)
        if (match) {
            window.location.href = match.baseUrl!
        }
    }

    async logout(): Promise<void> {
        await this.authenticationService.logout()
        await this.router.navigate(['login'])
    }

    async goHome(): Promise<void> {
        await this.router.navigate(['home'])
    }

    protected readonly window = window;
}
